import { useEffect, useRef, useCallback, useState } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 30000;

export function useWebSocket(userId, onMessage) {
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  // Hold the latest onMessage in a ref so callers can pass non-memoized
  // callbacks without causing reconnects on every render.
  const onMessageRef = useRef(onMessage);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  const [connected, setConnected] = useState(false);

  const connect = useCallback(() => {
    if (!userId) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}`;
    const url = `${wsUrl}/api/ws/${userId}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        // Start ping interval
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== 'pong' && onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (e) {
          // Non-JSON frame — not a protocol error, just ignore.
          console.debug('WS non-JSON frame ignored:', e?.message || e);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingRef.current);
        // Auto-reconnect
        reconnectRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      // Connect threw synchronously (e.g. bad URL, blocked by CSP).
      // Schedule a reconnect and surface to debug logs.
      console.debug('WS connect threw — retrying:', e?.message || e);
      reconnectRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
    }
  }, [userId]);  // intentionally excludes onMessage (held in ref)

  useEffect(() => {
    connect();
    return () => {
      clearInterval(pingRef.current);
      clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected };
}
